import { getUTMSources } from "@/components/listings/sidebar/lib/utm-content";
import type { api } from "@/lib/api-client";
import * as metaPixel from "@/lib/meta-pixel";
import { buildChatUrl } from "@/lib/url";
import type { DetailedListing } from "@/lib/types";
import type { BuyerProfileData } from "@/components/listings/buyer-profile-fields";
import type { InquiryResponse } from "@/components/intake/inquiry-intake-fields";
import type { useInquirySender } from "@/hooks/use-inquiry-sender";
import type { useErrorFormatter } from "@/hooks/use-error-formatter";
import type { useListingAnalytics } from "@/hooks/use-listing-analytics";
import type { useSearchParams } from "next/navigation";
import type { useTranslations } from "next-intl";
export type InquiryUser = Omit<
  NonNullable<ReturnType<typeof useInquirySender>["user"]>,
  "delegated"
>;
export type DialogStep =
  | "form"
  | "auth_start"
  | "auth_otp"
  | "auth_complete_profile"
  | "buyer_profile"
  | "submitting"
  | "success";

export function createInquirySubmit({
  listing,
  user,
  inlineName,
  inlinePhone,
  buyerProfile,
  intakeResponses,
  messageRef,
  apiUtils,
  sendMessageMutation,
  trackContactClick,
  searchParams,
  t,
  setError,
  setStep,
  setConversationUrl,
  onSent,
  displayErrorToast,
  formatError,
}: {
  listing: DetailedListing;
  user: InquiryUser | null;
  inlineName: string;
  inlinePhone: string;
  buyerProfile: BuyerProfileData;
  intakeResponses: Record<string, InquiryResponse>;
  messageRef: { current: string };
  apiUtils: ReturnType<typeof api.useUtils>;
  sendMessageMutation: ReturnType<typeof api.listings.sendMessage.useMutation>;
  trackContactClick: ReturnType<
    typeof useListingAnalytics
  >["trackContactClick"];
  searchParams: ReturnType<typeof useSearchParams>;
  t: ReturnType<typeof useTranslations>;
  setError: (error: string | null) => void;
  setStep: (step: DialogStep) => void;
  setConversationUrl: (url: string | null) => void;
  onSent?: () => void;
  displayErrorToast: ReturnType<typeof useErrorFormatter>["displayErrorToast"];
  formatError: ReturnType<typeof useErrorFormatter>["formatError"];
}) {
  return async function submitInquiry(submittingUser?: InquiryUser) {
    const targetUser = submittingUser ?? user;
    if (
      !targetUser ||
      targetUser.id === listing.ownerId ||
      targetUser.id === listing.sellerAgentId
    ) {
      return;
    }

    const effectiveName = targetUser.name || inlineName.trim();
    if (!effectiveName) {
      setError("Please enter your name to send an inquiry.");
      return;
    }

    setStep("submitting");
    setError(null);

    try {
      const inquiryGate = await apiUtils.listings.canInquire.fetch({
        listingId: listing.id,
      });
      if (!inquiryGate.allowed && inquiryGate.reason === "BROKER_BLOCK") {
        setError(t("saas.inquiryRestrictions.brokerNotAccepting"));
        setStep("form");
        return;
      }

      const existing =
        await apiUtils.listings.getListingInquiryConversation.fetch({
          id: listing.id,
        });

      if (existing.conversation) {
        setConversationUrl(buildChatUrl(existing.conversation));
        setStep("success");
        onSent?.();
        return;
      }

      try {
        await trackContactClick();
      } catch {
        // ignore
      }

      const leadSources = getUTMSources(listing.id);
      const currentUTMContent = searchParams.get("utm_content");
      if (currentUTMContent && !leadSources.includes(currentUTMContent)) {
        leadSources.push(currentUTMContent);
      }

      const payload: Parameters<typeof sendMessageMutation.mutateAsync>[0] = {
        id: listing.id,
        message: messageRef.current,
        leadSources,
      };

      if (!targetUser.name && inlineName.trim()) {
        payload.name = inlineName.trim();
      }
      if (!targetUser.phone && inlinePhone.trim()) {
        payload.phone = inlinePhone.trim();
      }
      if (buyerProfile.buyerTimeline) {
        payload.buyerTimeline = buyerProfile.buyerTimeline;
      }
      if (buyerProfile.buyerExperience) {
        payload.buyerExperience = buyerProfile.buyerExperience;
      }
      if (buyerProfile.buyerBudgetMin !== undefined) {
        payload.buyerBudgetMin = buyerProfile.buyerBudgetMin;
      }
      if (buyerProfile.buyerBudgetMax !== undefined) {
        payload.buyerBudgetMax = buyerProfile.buyerBudgetMax;
      }
      payload.intakeResponses = intakeResponses;

      const { conversation } = await sendMessageMutation.mutateAsync(payload);

      if (leadSources.length > 0) {
        metaPixel.event("Lead");
      }

      setConversationUrl(buildChatUrl(conversation));
      setStep("success");
      onSent?.();
    } catch (err) {
      console.error(err);
      displayErrorToast(err);
      setError(formatError(err));
      setStep("form");
    }
  };
}
